import { useState, useEffect, useCallback, useRef } from 'react';
import { sortByName } from '../../utils/sortingUtils';
import { RuleManager } from './components/RuleManager';
import { FeaturesTree } from './components/Features';
import NoActiveProject from '../../components/NoActiveProject';

/**
 * RulesPage - Main page for managing rules, rule tables, and features
 * Formerly known as ProductMasterDashboard
 */
export default function RulesPage() {
    const [isRightPaneOpen, setIsRightPaneOpen] = useState(true);
    const [isTableFocusMode, setIsTableFocusModeState] = useState(false);
    const setIsTableFocusMode = useCallback((value) => {
        setIsTableFocusModeState(prev => {
            const next = typeof value === 'function' ? value(prev) : value;
            if (next !== prev) {
                window.dispatchEvent(new CustomEvent('tableFocusModeChanged', {
                    detail: { focused: next }
                }));
            }
            return next;
        });
    }, []);
    const [refreshCount, setRefreshCount] = useState(0);
    const [rulesetSyncCount, setRulesetSyncCount] = useState(0);
    const [featuresMetadata, setFeaturesMetadata] = useState([]);
    const applyTreeSearchRef = useRef(() => {});
    const [currentContext, setCurrentContext] = useState({ id: '', name: 'No Family Selected' });

    const handleFeaturesRefresh = useCallback(() => {
        setRefreshCount(prev => prev + 1);
    }, []);

    const handleRuleCellsChanged = useCallback(() => {
        setRefreshCount(prev => prev + 1);
        setRulesetSyncCount(prev => prev + 1);
    }, []);

    const bindTreeSearch = useCallback((fn) => {
        applyTreeSearchRef.current = typeof fn === 'function' ? fn : () => {};
    }, []);

    // Family context is now handled in RuleManager's loadMenu via hierarchical endpoint
    // This removes the race condition with the separate API call

    const [activeProjectId, setActiveProjectId] = useState(localStorage.getItem('activeProjectId') || '');

    useEffect(() => {
        const handleGlobalProjectChange = () => {
            setActiveProjectId(localStorage.getItem('activeProjectId') || '');
            setCurrentContext({ id: '', name: 'No Family Selected' }); // Reset context on switch
            setIsTableFocusMode(false);
        };
        window.addEventListener('activeProjectChanged', handleGlobalProjectChange);
        return () => window.removeEventListener('activeProjectChanged', handleGlobalProjectChange);
    }, []);

    const handleFeaturesLoaded = useCallback((features) => {
        setFeaturesMetadata(sortByName(features || [], 'name'));
    }, []);

    if (!activeProjectId) {
        return (
            <NoActiveProject description="To configure families, rule tables, and features, select an active project in the header, or create one from the project menu." />
        );
    }

    return (
        <div style={{ display: 'flex', height: '100%', width: '100%', overflow: 'hidden', position: 'relative' }}>
            <button
                type="button"
                onClick={() => setIsRightPaneOpen(prev => !prev)}
                tabIndex={isTableFocusMode ? -1 : undefined}
                aria-hidden={isTableFocusMode}
                style={{
                    position: 'absolute',
                    right: isRightPaneOpen && !isTableFocusMode ? '300px' : '16px',
                    top: '5.4rem',
                    width: '40px',
                    height: '40px',
                    borderRadius: '999px',
                    border: '1px solid var(--border)',
                    background: 'var(--accent)',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 20px var(--accent-glow)',
                    transition: 'var(--transition)',
                    zIndex: 30,
                    opacity: isTableFocusMode ? 0 : 1,
                    pointerEvents: isTableFocusMode ? 'none' : 'auto'
                }}
                onMouseEnter={e => {
                    e.currentTarget.style.background = 'var(--accent-hover)';
                    e.currentTarget.style.transform = 'translateX(-1px)';
                }}
                onMouseLeave={e => {
                    e.currentTarget.style.background = 'var(--accent)';
                    e.currentTarget.style.transform = 'none';
                }}
                title={isRightPaneOpen ? 'Collapse Features' : 'Expand Features'}
                aria-label={isRightPaneOpen ? 'Collapse Features' : 'Expand Features'}
            >
                <i className="fa-sharp fa-light fa-box-open" style={{ fontSize: '1rem' }}></i>
            </button>

            <div className="fade-in" style={{ display: 'flex', flex: 1, minWidth: 0, height: '100%', overflow: 'hidden' }}>
                <RuleManager 
                    onDataChanged={handleFeaturesRefresh} 
                    featuresMetadata={featuresMetadata}
                    onFeaturesLoaded={handleFeaturesLoaded}
                    currentContext={currentContext}
                    setCurrentContext={setCurrentContext}
                    isRightPaneOpen={isRightPaneOpen}
                    setIsRightPaneOpen={setIsRightPaneOpen}
                    isTableFocusMode={isTableFocusMode}
                    setIsTableFocusMode={setIsTableFocusMode}
                    rulesetSyncCount={rulesetSyncCount}
                    featuresRefreshCount={refreshCount}
                    onBindTreeSearch={bindTreeSearch}
                />
                
                {isRightPaneOpen && (
                    <div
                        className={`rules-focus-clip${isTableFocusMode ? ' is-collapsed' : ''}`}
                        aria-hidden={isTableFocusMode}
                        inert={isTableFocusMode || undefined}
                    >
                        <div
                            className="rules-focus-clip-inner"
                            style={{
                                borderLeft: '1px solid var(--border)',
                                background: 'var(--bg-card)',
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden'
                            }}
                        >
                            <div style={{
                                padding: '1.5rem',
                                borderBottom: '1px solid var(--border)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.5rem',
                                background: 'var(--bg-card)'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                    <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Features</h2>
                                </div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                    Context: <strong style={{ color: 'var(--accent)' }}>{currentContext.name}</strong>
                                </div>
                            </div>
                            <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>
                                <FeaturesTree
                                    features={featuresMetadata}
                                    currentContext={currentContext}
                                    onRefresh={handleFeaturesRefresh}
                                    onRuleCellsChanged={handleRuleCellsChanged}
                                    onSearchFeature={(name) => applyTreeSearchRef.current?.('feature', name)}
                                    onSearchValue={(name, value) => applyTreeSearchRef.current?.('value', name, value)}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
